<script setup lang="ts">
import { RouteMeta } from 'vue-router'

import BSnackbarQueue from '../BSnackbarQueue/BSnackbarQueue.vue'

import { useBreadcrumb } from '../../composables/use-breadcrumb'
import { useDefaults } from '../../composables/use-defaults'
import { Layout, useLayout } from '../../composables/use-layout'
import { BHeaderEvents, BHeaderProps } from '../BHeader/use-header'
import { BSidebarProps } from '../BSidebar/use-sidebar'

const BTopAppBar = defineAsyncComponent(
	() => import('../BTopAppBar/BTopAppBar.vue')
)
const BSidebar = defineAsyncComponent(() => import('../BSidebar/BSidebar.vue'))
const BHeader = defineAsyncComponent(() => import('../BHeader/BHeader.vue'))
const BBreadcrumb = defineAsyncComponent(
	() => import('../BBreadcrumb/BBreadcrumb.vue')
)
const BIcon = defineAsyncComponent(() => import('../BIcon/BIcon.vue'))

const _props = defineProps<{
	sidebarProps?: BSidebarProps
	headerProps?: BHeaderProps
}>()
const props = useDefaults(_props)
const emits = defineEmits<BHeaderEvents>()

const breadcrumb = useBreadcrumb()
const layoutInstance = useLayout()
const router = useRouter()
const { t, te } = useI18n()

const onRouteMetaChange = (meta: RouteMeta) => {
	layoutInstance.setOnBackClick(null)

	if (meta.layout) {
		if (typeof meta.layout === 'string') {
			layoutInstance.setLayout(meta.layout as Layout)
			layoutInstance.setTitle(meta.title || null)
		} else {
			layoutInstance.setLayout(meta.layout.name)
			layoutInstance.setHideBackBtn(meta.layout.hideBackBtn)
			layoutInstance.setTitle(meta.layout.title || meta.title || null)
			layoutInstance.setEndIcon(meta.layout.endIcon)
			layoutInstance.setEndIconSize(meta.layout.endIconSize)
		}

		return
	}

	layoutInstance.setLayout(null)
	layoutInstance.setHideBackBtn(false)
	layoutInstance.setTitle(meta.title || null)
	layoutInstance.setEndIcon(null)
	layoutInstance.setEndIconSize(null)
	layoutInstance.setOnEndIconClick(null)
}

const route = useRoute()
watch(
	() => route.meta,
	(meta) => onRouteMetaChange(meta),
	{ immediate: true, deep: true }
)

const title = computed(() =>
	layoutInstance.title.value
		? te(layoutInstance.title.value)
			? t(layoutInstance.title.value)
			: layoutInstance.title.value
		: null
)

const windowHistoryState = ref(structuredClone(window.history.state))
router.afterEach(() => {
	windowHistoryState.value = structuredClone(window.history.state)
})

const backRouteExists = computed(() => !!windowHistoryState.value.back)

const backIcon = computed(
	() => !layoutInstance.hideBackBtn.value && backRouteExists.value
)
const back = () => {
	if (layoutInstance.onBackClick.value) {
		layoutInstance.onBackClick.value()
		return
	}

	router.back()
}

const onEndIconClick = () => {
	if (layoutInstance.onEndIconClick.value) {
		layoutInstance.onEndIconClick.value()
		return
	}

	console.warn('[BLayout] onEndIconClick is not passed!')
}

const routerIsReady = ref(false)
router.isReady().then(() => (routerIsReady.value = true))
</script>

<template>
	<template v-if="routerIsReady">
		<div
			v-if="layoutInstance.name.value === 'panel'"
			key="panel-layout-wrapper"
			class="flex gap-4 pb-6 pe-6 ps-2"
		>
			<BSidebar
				v-if="props.sidebarProps"
				v-bind="props.sidebarProps"
				class="sticky top-0 pt-6 print:hidden"
				style="height: calc(100dvh - var(--spacing) * 6)"
			/>

			<div class="grow relative">
				<div
					class="sticky top-0 w-full pt-6 bg-linear-to-b from-body-background via-90% via-body-background to-transparent z-150 pb-4"
				>
					<BHeader
						v-bind="props.headerProps"
						class="ps-4 mb-6 print:hidden"
						@click:menu-item="emits('click:menu-item', $event)"
					/>

					<BBreadcrumb
						:items="breadcrumb.items.value"
						class="print:hidden"
					/>
				</div>

				<slot />
			</div>
		</div>

		<template v-else>
			<BTopAppBar
				v-if="layoutInstance.name.value === 'app-bar'"
				:title
				:back-icon
				@back="back"
			>
				<template
					v-if="layoutInstance.endIcon.value"
					#end
				>
					<BIcon
						:icon="layoutInstance.endIcon.value"
						:size="layoutInstance.endIconSize.value || undefined"
						role="button"
						color="on-surface"
						aria-label="Secondary action"
						data-test="end-icon"
						@click="onEndIconClick"
					/>
				</template>
			</BTopAppBar>

			<slot />
		</template>
	</template>

	<BSnackbarQueue />
</template>
